import { useLayoutEffect, useRef, type ReactElement } from "react";
import { logical } from "../../ui/kit/index.ts";
import { say } from "../../words.ts";
import { slotRect } from "../hud/area-slots.ts";
import type { HudArea } from "../hud/slots.ts";
import { useTravel, type TravelControl } from "./map-panel.tsx";

/**
 * The travel channel's bar in the HUD's cast slot (ADR-137 decision 23; the design's `CastBar`, a visual reference):
 * "Travelling to <place>" on the track with the seconds left at its right, the brass fill growing to full over the
 * channel's ticks, and on a break the bar full in the danger's dark fill reading "Travel interrupted" a moment. The fill
 * and the seconds are written through refs only when the tenth of a second shown changes, so a channel re-renders
 * nothing and builds no string on the frames between, and the frames stop when the channel's time is up. Reduced
 * motion changes nothing: the bar is not motion.
 *
 * @param props The `area` it is laid out on and the `travel` store it reads.
 * @returns The bar, or nothing while no channel runs.
 */
export function TravelBar({ area, travel }: { readonly area: HudArea; readonly travel: TravelControl }): ReactElement | null {
  const snapshot = useTravel(travel);
  const fill = useRef<HTMLDivElement>(null);
  const seconds = useRef<HTMLSpanElement>(null);
  const bar = snapshot?.bar ?? { kind: "none" as const };

  useLayoutEffect(() => {
    if (bar.kind !== "channel") {
      return undefined;
    }
    let frame = 0;
    let shownTenths = -1;
    const span = Math.max(1, bar.endsMs - bar.startedMs);
    const step = (): void => {
      const left = Math.max(0, bar.endsMs - performance.now());
      const tenths = Math.ceil(left / 100);
      if (tenths !== shownTenths) {
        shownTenths = tenths;
        if (fill.current !== null) {
          fill.current.style.width = `${Math.min(100, ((span - tenths * 100) / span) * 100)}%`;
        }
        if (seconds.current !== null) {
          seconds.current.textContent = `${(tenths / 10).toFixed(1)}s`;
        }
      }
      frame = left > 0 ? requestAnimationFrame(step) : 0;
    };
    step();
    return () => cancelAnimationFrame(frame);
  }, [bar]);

  if (bar.kind === "none") {
    return null;
  }
  const rect = slotRect("cast", area);
  const interrupted = bar.kind === "interrupted";
  const words = interrupted ? say("travel.interrupted") : say("travel.channel", { name: bar.name });
  return (
    <div className="ot-hud-slot" data-hud-slot="cast" style={{ left: logical(rect.left), top: logical(rect.top), width: logical(rect.width), height: logical(rect.height) }}>
      <div className={interrupted ? "ot-travel-bar is-interrupted" : "ot-travel-bar"} data-hud="travel" data-state={bar.kind} role="progressbar" aria-label={words}>
        <div key={bar.kind} ref={fill} className="ot-travel-bar__fill" />
        <span className="ot-travel-bar__words">
          <span>{words}</span>
          {interrupted ? null : <span ref={seconds} />}
        </span>
      </div>
    </div>
  );
}
